/**
 * Scheduling model — shared by ChooseChow checkout, subscriptions, and the
 * order-thread messaging copy. Kept React-free so the API client and tests can
 * use it. The backend carries the chosen instant as `scheduled_at` on the order
 * / subscription (see `marketplace.proto` and `billing.proto`); an absent slot
 * means on-demand "as soon as possible" fulfilment.
 */

export interface DeliverySlot {
  /** Stable id for React keys / selection. */
  id: string
  /** ISO-8601 instant the slot starts (sent as `scheduled_at`). */
  startsAt: string
  /** Short human label, e.g. "Today · 6:30 PM". */
  label: string
  /** True for the implicit on-demand option (no `scheduled_at`). */
  asap: boolean
}

const HOUR = 60 * 60 * 1000
const MINUTE = 60 * 1000
const PLAIN_DATE = /^(\d{4})-(\d{2})-(\d{2})$/
const WALL_TIME = /^([01]\d|2[0-3]):([0-5]\d)$/

export interface DeliveryScheduleWindow {
  minimumLeadMinutes?: number
  intervalMinutes?: number
  serviceStart?: string
  serviceEnd?: string
}

export interface ScheduledParts {
  date: string
  time: string
}

/** Local calendar day without the UTC shift caused by `toISOString().slice`. */
export const toLocalDateKey = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(
    date.getDate(),
  ).padStart(2, '0')}`

export const toLocalTimeKey = (date: Date): string =>
  `${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`

export const parseLocalDateKey = (value: string): Date | null => {
  const match = PLAIN_DATE.exec(value)
  if (!match) return null
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return date.getFullYear() === Number(match[1]) &&
    date.getMonth() === Number(match[2]) - 1 &&
    date.getDate() === Number(match[3])
    ? date
    : null
}

const timeToMinutes = (value: string): number | null => {
  const match = WALL_TIME.exec(value)
  return match ? Number(match[1]) * 60 + Number(match[2]) : null
}

const boundedMinutes = (value: number, fallback: number): number =>
  Number.isFinite(value) ? Math.min(720, Math.max(1, Math.round(value))) : fallback

/** Ceil in local wall time; flooring here used to violate the lead-time promise. */
export const ceilToInterval = (date: Date, intervalMinutes = 30): Date => {
  const interval = boundedMinutes(intervalMinutes, 30)
  const next = new Date(date)
  const remainder = (next.getHours() * 60 + next.getMinutes()) % interval
  const hasPartialMinute = next.getSeconds() !== 0 || next.getMilliseconds() !== 0
  if (remainder !== 0 || hasPartialMinute) {
    next.setMinutes(next.getMinutes() + (interval - remainder), 0, 0)
  } else {
    next.setSeconds(0, 0)
  }
  return next
}

/** Earliest selectable instant, clamped into the configured daily service window. */
export const nextAvailableDelivery = (
  now: Date = new Date(),
  window: DeliveryScheduleWindow = {},
): Date => {
  const requestedLead = window.minimumLeadMinutes ?? 120
  const lead = Number.isFinite(requestedLead)
    ? Math.min(30 * 24 * 60, Math.max(0, Math.round(requestedLead)))
    : 120
  const interval = boundedMinutes(window.intervalMinutes ?? 30, 30)
  const serviceStart = timeToMinutes(window.serviceStart ?? '08:00') ?? 8 * 60
  const serviceEnd = timeToMinutes(window.serviceEnd ?? '22:00') ?? 22 * 60
  const candidate = ceilToInterval(new Date(now.getTime() + lead * MINUTE), interval)
  const minutes = candidate.getHours() * 60 + candidate.getMinutes()

  if (minutes < serviceStart) {
    candidate.setHours(Math.floor(serviceStart / 60), serviceStart % 60, 0, 0)
  } else if (minutes > serviceEnd) {
    candidate.setDate(candidate.getDate() + 1)
    candidate.setHours(Math.floor(serviceStart / 60), serviceStart % 60, 0, 0)
  }
  return candidate
}

export const splitScheduledAt = (value: string | undefined): ScheduledParts | null => {
  if (!value) return null
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return null
  return { date: toLocalDateKey(parsed), time: toLocalTimeKey(parsed) }
}

/** Combine explicit local date + wall time and serialize the actual instant as RFC3339/ISO. */
export const combineScheduledAt = (dateValue: string, timeValue: string): string | null => {
  const date = parseLocalDateKey(dateValue)
  const minutes = timeToMinutes(timeValue)
  if (!date || minutes === null) return null
  date.setHours(Math.floor(minutes / 60), minutes % 60, 0, 0)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

export const addLocalDays = (date: Date, days: number): Date => {
  const next = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  next.setDate(next.getDate() + Math.trunc(days))
  return next
}

/** Minimum wall time for a selectable day, or `null` when the day is too early. */
export const minimumTimeForDate = (
  dateValue: string,
  now: Date = new Date(),
  window: DeliveryScheduleWindow = {},
): string | null => {
  const earliest = nextAvailableDelivery(now, window)
  const earliestDay = toLocalDateKey(earliest)
  if (dateValue < earliestDay) return null
  return dateValue === earliestDay ? toLocalTimeKey(earliest) : window.serviceStart ?? '08:00'
}

const dayLabel = (date: Date, now: Date): string => {
  const sameDay = date.toDateString() === now.toDateString()
  const tomorrow = new Date(now.getTime() + 24 * HOUR)
  if (sameDay) return 'Today'
  if (date.toDateString() === tomorrow.toDateString()) return 'Tomorrow'
  return date.toLocaleDateString(undefined, { weekday: 'short' })
}

const timeLabel = (date: Date): string =>
  date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })

/**
 * Build the slot picker: an on-demand option followed by the next `count`
 * rounded slots on the hour, starting at least `leadHours` out.
 */
export const buildDeliverySlots = (
  now: Date = new Date(),
  count = 6,
  leadHours = 2,
  stepHours = 2,
): DeliverySlot[] => {
  const asap: DeliverySlot = { id: 'asap', startsAt: '', label: 'As soon as possible', asap: true }
  const first = nextAvailableDelivery(now, {
    minimumLeadMinutes: Math.max(0, leadHours) * 60,
    intervalMinutes: 60,
    serviceStart: '00:00',
    serviceEnd: '23:59',
  })
  const slotCount = Number.isFinite(count) ? Math.min(100, Math.max(0, Math.trunc(count))) : 6
  const step = Number.isFinite(stepHours) ? Math.min(24, Math.max(1, stepHours)) : 2

  const slots: DeliverySlot[] = [asap]
  for (let i = 0; i < slotCount; i += 1) {
    const start = new Date(first.getTime() + i * step * HOUR)
    slots.push({
      id: start.toISOString(),
      startsAt: start.toISOString(),
      label: `${dayLabel(start, now)} · ${timeLabel(start)}`,
      asap: false,
    })
  }
  return slots
}

/** Human summary of a chosen slot for confirmations and order rows. */
export const formatScheduled = (scheduledAt: string | undefined): string => {
  if (!scheduledAt) return 'Delivering as soon as possible'
  const date = new Date(scheduledAt)
  if (Number.isNaN(date.getTime())) return 'Scheduled delivery'
  return `Scheduled for ${dayLabel(date, new Date())} · ${timeLabel(date)}`
}

/**
 * Role-aware messaging copy for a scheduled order, surfaced in the order thread
 * and inbox. Customers see the promise; chefs see the prep obligation.
 */
export const scheduleMessage = (
  role: 'customer' | 'chef',
  scheduledAt: string | undefined,
): string => {
  if (!scheduledAt) {
    return role === 'chef'
      ? 'On-demand order — start prep now.'
      : "We'll get this to you as soon as possible."
  }
  const when = formatScheduled(scheduledAt).replace(/^Scheduled for /, '')
  return role === 'chef'
    ? `Scheduled order — have it ready for ${when}.`
    : `Your order is booked for ${when}. We'll remind you before it's out.`
}
